"use client";

import React, { Suspense, useCallback, useRef } from "react";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ThemeProvider } from "@mui/material";
import { FeedBackProvider } from "../providers/FeedBackProvider/FeedBackProvider";
import { ClientsFiltersProvider } from "@/providers/ClientsFiltersContext";
import theme from "./theme";
import { EarningsFiltersProvider } from "@/providers/EarningsFiltersContext";
import { axios } from "../utils/axios.create";
import { BackofficeTimezoneSync } from "@/providers/BackofficeTimezoneSync";

function Providers({ children }: React.PropsWithChildren) {
  const [client] = React.useState(new QueryClient());

  // adding variables to adjust the chat colors in the css
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      "--outgoing-message-bg",
      theme.palette.surface.light2 || "#E7F3FF",
    );
    document.documentElement.style.setProperty(
      "--incoming-message-bg",
      theme.palette.surface.main || "#B5E0FF",
    );
    document.documentElement.style.setProperty(
      "--send-message-button-bg",
      theme.palette.primary.main || "#355DE8",
    );
  }, [theme]);

  const { QlikEmbedConfig } = require("@qlik/embed-react");

  const tokenRef = useRef(null);

  const getAccessToken = useCallback(async () => {
    if (tokenRef.current) {
      return tokenRef.current;
    }
    try {
      const response = await axios.post(
        "/users/qlik-token?email=dummy_email@email.com",
        {},
      );
      tokenRef.current = response.data.body.qlikToken;
      return tokenRef.current;
    } catch (error) {
      console.error("Error fetching access token", error);
      throw error;
    }
  }, []); // no dependency on token

  const hostConfig = {
    host: "https://cmi.us.qlikcloud.com",
    authType: "oauth2",
    clientId: process.env.REACT_APP_QLIK_CLIENT_ID,
    noCache: true,
    getAccessToken: getAccessToken,
  };

  return (
    <QlikEmbedConfig.Provider value={hostConfig}>
      <QueryClientProvider client={client}>
        <BackofficeTimezoneSync />
        <ThemeProvider theme={theme}>
          <ClientsFiltersProvider>
            <EarningsFiltersProvider>
              <FeedBackProvider>
                <Suspense fallback={<div>Loading...</div>}>{children}</Suspense>
              </FeedBackProvider>
            </EarningsFiltersProvider>
          </ClientsFiltersProvider>
        </ThemeProvider>
        <ReactQueryDevtools
          buttonPosition="bottom-left"
          initialIsOpen={false}
        />
      </QueryClientProvider>
    </QlikEmbedConfig.Provider>
  );
}

export default Providers;
